import { stripHtml } from 'string-strip-html'
import { isAppContext } from './appHelper'
import { AppContext } from '../config/appConfig'
import type { AppToast } from '../hooks/useAppToast'

/**
 * Reports an Office error to the user.
 *
 * The toast has to be passed in because this module is not a component. Without one the error is only
 * logged — the helpers below call this as a last resort when the caller supplied no error callback of
 * its own, and threading a toast through every one of their call sites is not worth it.
 */
export const makeOfficeErrorToast = (toast?: AppToast, result?: Office.AsyncResult<void | string>, error?: Error) => {
    const message = error ? `${error.message}` : result?.error ? `${result.error.code}: ${result.error.message}` : undefined

    if (!message) {
        return
    }

    if (toast) {
        toast.showMessage(message, { timeout: 1000 })
    } else {
        console.error(message)
    }
}

export function outlookDisplayNewMessageForm(
    parameters: Office.MessageForm,
    callback?: (asyncResult?: Office.AsyncResult<void>) => void,
    errorCallback?: (asyncResult?: Office.AsyncResult<void>, error?: Error) => void,
    toast?: AppToast,
): void {
    try {
        if ((!isOfficeInitialized() || isAppContext([AppContext.Compose])) && !(parameters.attachments?.length > 0)) {
            parameters.htmlBody =
                parameters.htmlBody &&
                stripHtml(parameters.htmlBody, {
                    dumpLinkHrefsNearby: {
                        enabled: true,
                        putOnNewLine: false,
                        wrapHeads: '[',
                        wrapTails: ']',
                    },
                }).result

            window.location.href = [
                `mailto:${parameters.toRecipients.map(att => att.emailAddress).join(';')}`,
                parameters.bccRecipients ? `&bcc=${parameters.bccRecipients.map(att => att.emailAddress).join(';')}` : undefined,
                `?subject=${encodeURIComponent(parameters.subject) || ''}`,
                parameters.htmlBody ? `&body=${encodeURIComponent(parameters.htmlBody)}` : undefined,
            ]
                .filter(value => value !== undefined)
                .join('')

            callback?.(undefined)
        } else {
            assertOfficeInitialized()

            parameters.subject = parameters.subject?.substring(0, Math.min(parameters.subject.length, 255))
            parameters.htmlBody = parameters.htmlBody?.replaceAll('\n', '<br/>')
            parameters.htmlBody = parameters.htmlBody?.substring(0, Math.min(parameters.htmlBody?.length, 16380))

            if (Office.context.requirements.isSetSupported('Mailbox', '1.9') && Office.context.mailbox?.displayNewMessageFormAsync) {
                Office.context.mailbox.displayNewMessageFormAsync(parameters, asyncResult => {
                    if (!asyncResult || asyncResult?.status === Office.AsyncResultStatus.Failed) {
                        console.log(`Couldn't open new e-mail dialog: `, asyncResult?.error?.message)
                        if (!errorCallback) {
                            makeOfficeErrorToast(toast, asyncResult, asyncResult?.error as Error)
                        }
                        errorCallback?.(asyncResult, asyncResult?.error as Error)
                    } else {
                        callback?.(asyncResult)
                    }
                })
            } else {
                Office.context.mailbox.displayNewMessageForm(parameters)
                callback?.()
            }
        }
    } catch (error) {
        console.log('displayNewMessageForm: ', error)
        if (!errorCallback) {
            makeOfficeErrorToast(toast, undefined, error)
        }
        errorCallback?.(undefined, error)
    }
}

export function outlookDisplayNewAppointmentForm(
    parameters: Office.AppointmentForm,
    callback?: (asyncResult?: Office.AsyncResult<void>) => void,
    errorCallback?: (asyncResult?: Office.AsyncResult<void>, error?: Error) => void,
    toast?: AppToast,
): void {
    try {
        if (!isOfficeInitialized() || isAppContext([AppContext.Compose])) {
            window.location.href = [
                `webcal:${parameters.requiredAttendees.map(att => att.emailAddress).join(';')}`,
                `?subject=${encodeURIComponent(parameters.subject) || ''}`,
            ]
                .filter(value => value !== undefined)
                .join('')

            callback?.()
        } else {
            assertOfficeInitialized()

            parameters.subject = parameters.subject?.substring(0, Math.min(parameters.subject.length, 255))
            parameters.body = parameters.body?.replaceAll('\n', '<br/>')
            parameters.body = parameters.body?.substring(0, Math.min(parameters.body?.length, 16380))

            if (Office.context.requirements.isSetSupported('Mailbox', '1.9') && Office.context.mailbox?.displayNewAppointmentFormAsync) {
                Office.context.mailbox.displayNewAppointmentFormAsync(parameters as Office.AppointmentForm, asyncResult => {
                    if (!asyncResult || asyncResult?.status === Office.AsyncResultStatus.Failed) {
                        console.log(`Couldn't open new appointment dialog: `, asyncResult?.error?.message)
                        if (!errorCallback) {
                            makeOfficeErrorToast(toast, asyncResult, asyncResult?.error as Error)
                        }
                        errorCallback?.(asyncResult, asyncResult?.error as Error)
                    } else {
                        callback?.(asyncResult)
                    }
                })
            } else {
                Office.context.mailbox.displayNewAppointmentForm(parameters as Office.AppointmentForm)
                callback?.()
            }
        }
    } catch (error) {
        console.log('displayNewAppointmentForm: ', error)
        if (!errorCallback) {
            makeOfficeErrorToast(toast, undefined, error)
        }
        errorCallback?.(undefined, error)
    }
}

export function outlookDisplayMessageForm(
    messageId: string,
    callback?: (asyncResult?: Office.AsyncResult<void>) => void,
    errorCallback?: (asyncResult?: Office.AsyncResult<void>, error?: Error) => void,
    toast?: AppToast,
): void {
    try {
        if (isOfficeInitialized() && messageId) {
            if (Office.context.requirements.isSetSupported('Mailbox', '1.9') && Office.context.mailbox?.displayMessageFormAsync) {
                Office.context.mailbox.displayMessageFormAsync(messageId, asyncResult => {
                    if (asyncResult?.status === Office.AsyncResultStatus.Failed) {
                        console.log(`Couldn't open message dialog: `, asyncResult?.error?.message)
                        if (!errorCallback) {
                            makeOfficeErrorToast(toast, asyncResult, asyncResult?.error as Error)
                        }
                        errorCallback?.(asyncResult, asyncResult?.error as Error)
                    } else {
                        callback?.(asyncResult)
                    }
                })
            } else {
                Office.context.mailbox.displayMessageForm(messageId)
                callback?.()
            }
        }
    } catch (error) {
        console.log('displayMessageForm: ', error)
        if (!errorCallback) {
            makeOfficeErrorToast(toast, undefined, error)
        }
        errorCallback?.(undefined, error)
    }
}

export function addFileAttachmentAsync(
    downloadUrl: string,
    name: string,
    callback?: (asyncResult?: Office.AsyncResult<void | string>) => void,
    errorCallback?: (asyncResult?: Office.AsyncResult<void | string>, error?: Error) => void,
    toast?: AppToast,
): void {
    try {
        if (isOfficeInitialized() && downloadUrl) {
            if (Office.context.requirements.isSetSupported('Mailbox', '1.8') && Office.context.mailbox?.item?.addFileAttachmentAsync) {
                Office.context.mailbox.item.addFileAttachmentAsync(downloadUrl, name, asyncResult => {
                    if (!asyncResult || asyncResult?.status === Office.AsyncResultStatus.Failed) {
                        console.log(`Couldn't add attachments: `, asyncResult?.error?.message)
                        if (!errorCallback) {
                            makeOfficeErrorToast(toast, asyncResult, asyncResult?.error as Error)
                        }
                        errorCallback?.(asyncResult, asyncResult?.error as Error)
                    } else {
                        callback?.(asyncResult)
                    }
                })
            } else {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error(
                    `This operation cannot be performed with this version of Outlook (version ${Office.context.diagnostics.platform}/${Office.context.diagnostics.host}/${Office.context.diagnostics.version}). Either the RequirementSet 1.1+ is not met or the method 'addFileAttachmentAsync' is not available, both should not happen with an up to date version of Outlook.`,
                )
            }
        }
    } catch (error) {
        console.log('addFileAttachmentAsync: ', error)
        if (!errorCallback) {
            makeOfficeErrorToast(toast, undefined, error)
        }
        errorCallback?.(undefined, error)
    }
}

export function isOfficeInitialized(): boolean {
    return Office.hasOwnProperty('context') && Office.context.hasOwnProperty('ui')
}

export function isOfficeApplication(): boolean {
    return isOfficeInitialized() && [Office.PlatformType.PC, Office.PlatformType.Mac].includes(Office.context.platform)
}

export function assertOfficeInitialized(): void {
    if (!isOfficeInitialized()) {
        throw new Error('Deze handeling kan alleen vanuit Outlook worden uitgevoerd.')
    }
}
